import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X, Save, Trash2 } from 'lucide-react';
import { Button } from './button';

/**
 * UnsavedChangesModal - Shows what changed and gives clear options
 * 
 * Props:
 * - isOpen: boolean - whether modal is visible
 * - onClose: () => void - close modal (keep editing)
 * - onDiscard: () => void - discard changes and leave
 * - onSave: () => void - save changes then leave
 * - changes: Array<{ field: string, oldValue: any, newValue: any }> - list of changes
 * - saving: boolean - whether save is in progress
 */
const UnsavedChangesModal = ({ 
  isOpen, 
  onClose, 
  onDiscard, 
  onSave, 
  changes = [],
  saving = false,
  title = "Unsaved Changes",
  message = "You have unsaved changes:"
}) => {
  if (!isOpen) return null;

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Format field name for display
  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  // Use React Portal to render at document.body level, above all other overlays
  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center" 
      style={{ 
        zIndex: 99999, 
        pointerEvents: 'auto',
        isolation: 'isolate'
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('🔲 BACKDROP CLICKED');
          onClose();
        }}
        style={{ pointerEvents: 'auto' }}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-gray-800 border border-gray-700 rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-900/50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h3 className="text-base font-semibold text-white">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Keep Editing"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4">
          <p className="text-sm text-gray-300 mb-3">
            {message}
          </p>
          
          {/* Changes List */}
          {changes.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {changes.map((change, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-2 text-xs bg-gray-900/50 rounded px-3 py-2"
                >
                  <span className="text-amber-400 mt-0.5">•</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-400">{formatFieldName(change.field)}:</span>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      <span className="text-red-400 line-through truncate max-w-[120px]" title={formatValue(change.oldValue)}>
                        {formatValue(change.oldValue)}
                      </span>
                      <span className="text-gray-500">→</span>
                      <span className="text-green-400 truncate max-w-[120px]" title={formatValue(change.newValue)}>
                        {formatValue(change.newValue)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-400 py-2">
              Your unsaved pricing changes will be lost.
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700" />

        {/* Footer */}
        <div className="px-4 py-3 flex items-center justify-end gap-2 bg-gray-900/30" style={{ pointerEvents: 'auto' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              console.log('🗑️ DISCARD BUTTON CLICKED');
              onDiscard();
            }}
            disabled={saving}
            className="text-gray-400 hover:text-white hover:bg-gray-700"
            data-testid="discard-changes-btn"
            style={{ pointerEvents: 'auto' }}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Discard Changes
          </Button>
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              console.log('💾 SAVE & CONTINUE BUTTON CLICKED');
              onSave();
            }}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="save-and-continue-btn"
            style={{ pointerEvents: 'auto' }}
          >
            {saving ? (
              <>
                <span className="animate-spin mr-1.5">⏳</span>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5 mr-1.5" />
                Save & Continue
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Render via React Portal to escape any parent stacking contexts
  return createPortal(modalContent, document.body);
};

export default UnsavedChangesModal;
